import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { ref, shallowRef, type Ref, type ShallowRef } from 'vue'

/**
 * Return type for the useAxios composable
 * Provides request execution function, response data, error handling,
 * status tracking, loading state, and direct access to axios instance.
 */
interface UseAxiosReturn<T> {
  execute: (url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<T> | undefined>
  data: ShallowRef<T | null>
  error: ShallowRef<AxiosError | null>
  status: Ref<number | null>
  response: ShallowRef<AxiosResponse<T> | null>
  isLoading: Ref<boolean>
  axiosInstance: AxiosInstance
}

/**
 * Custom Axios composable with error handling, authentication and loading state
 * @param options - Optional Axios configuration
 * @returns Object with request execution function and reactive state
 */
export function useAxios<T = unknown>(options?: AxiosRequestConfig): UseAxiosReturn<T> {
  // Reactive state
  const data = shallowRef<T | null>(null)
  const error = shallowRef<AxiosError | null>(null)
  const status = ref<number | null>(null)
  const response = shallowRef<AxiosResponse<T> | null>(null)
  const isLoading = ref(false)

  // Create Axios instance with default configuration
  const axiosInstance = axios.create({
    baseURL: import.meta.env.VUE_API_BASE_URL || 'http://localhost:8000/api/',
    maxRedirects: 0,
    responseType: 'json',
    headers: {
      common: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Frame-Options': 'DENY'
      }
    },
    xsrfHeaderName: 'X-CSRFTOKEN',
    xsrfCookieName: 'csrftoken',
    withCredentials: true,
    ...options // Override defaults with provided options
  })

  /**
   * Execute an HTTP request and handle state management
   * @param url - The URL to request
   * @param config - Optional request configuration
   * @returns Promise with the response or undefined on error
   */
  const execute = async (url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T> | undefined> => {
    // Reset state before request
    data.value = null
    error.value = null
    status.value = null
    response.value = null
    isLoading.value = true

    try {
      const result = await axiosInstance(url, { ...options, ...config })

      // Update state with successful response
      data.value = result.data
      status.value = result.status
      response.value = result

      return result
    } catch (err) {
      // Ensure error state is set if interceptor somehow missed it
      if (!error.value && err instanceof AxiosError) {
        error.value = err
        status.value = err.response?.status || null
      } else if (!error.value) {
        error.value = err as AxiosError
      }

      return undefined
    } finally {
      isLoading.value = false
    }
  }

  return {
    execute,
    data,
    error,
    status,
    response,
    isLoading,
    axiosInstance
  }
}
